/*
 *  Copyright (c) Qwoach LLC 2021. All rights reserved.
 *  Unauthorized copying of this file, via any medium is strictly prohibited
 *  Proprietary and confidential
 *  Created by Artem Bogush <artem@qwoach.com> on 13/12/2021
 */

import BaseObject from "@shared/BaseObject.js";
import {GenogramConnectionCategoryEnum} from "@shared/Genogram/GenogramConnectionCategoryEnum";


class LineTypeItem extends BaseObject {

	title: string = null
	lineType: string = null
	lastUsedAt: number = 0

	isSelected: boolean = false
	previewSrc: string = ""
	isCustomConnection: boolean = false
	customConnectionId: string = null
	connectionCategory: string = GenogramConnectionCategoryEnum.EMOTIONAL


	constructor(title: string = null, lineType: string = null, lineStyle = null, connectionCategory = GenogramConnectionCategoryEnum.EMOTIONAL) {
		super();
		this.title = title;
		this.lineType = lineType;
		this.lineStyle = lineStyle
		this.connectionCategory = connectionCategory
	}

	setLastUsedNow(){
		this.lastUsedAt = new Date().getTime()
	}

	getLineTypeFull(){
		return this.lineType + (this.lineSubType ? '-' + this.lineSubType : "")
	}
}



let _styleDefault = {
	strokeStyle: "rgb(153, 153, 153)",
	strokeStyleSelected: "rgb(100, 100, 100)",
	lineWidth: 4
}
let _styleHarmony = {
	strokeStyle: "rgb(100, 200, 10)",
	strokeStyleSelected: "rgb(80, 180, 8)",
	lineWidth: 4
}
let _styleConflict = {
	strokeStyle: "rgb(201, 11, 11)",
	strokeStyleSelected: "rgb(151, 11, 11)",
	lineWidth: 4
}
let _styleAbuse = {
	strokeStyle: "rgb(10, 10, 200)",
	strokeStyleSelected: "rgb(8, 8, 180)",
	lineWidth: 4
}
let _styleFamily = {
	strokeStyle: "rgb(151, 11, 11)",
	strokeStyleSelected: "rgb(100, 7, 7)",
	lineWidth: 4
}
let _styleFamily2 = {
	strokeStyle: "rgb(10, 10, 200)",
	strokeStyleSelected: "rgb(8, 8, 180)",
	lineWidth: 4
}
let _styleFamily3 = {
	strokeStyle: "rgb(255, 160, 180)",
	strokeStyleSelected: "rgb(205, 100, 120)",
	lineWidth: 4
}

let oriented = function (lineStyle){
	return {
		...lineStyle,
		isOriented: true
	}
}

let dotted = function (lineStyle){
	let dot = lineStyle.lineWidth
	let gap = 3 * lineStyle.lineWidth
	return {...lineStyle, lineDash: [dot, gap]}
}

let dashed = function (lineStyle){
	let dash = 3 * lineStyle.lineWidth
	let gap = 3 * lineStyle.lineWidth
	return {...lineStyle, lineDash: [dash, gap]}
}
let dotDashed = function (lineStyle){
	let dash = 4 * lineStyle.lineWidth
	let dot = lineStyle.lineWidth
	let gap = 3 * lineStyle.lineWidth
	return {...lineStyle, lineDash: [dash, gap, dot, gap]}
}
let dotDotDashed = function (lineStyle, gapMultiplier = 1){
	let dash = 4 * lineStyle.lineWidth
	let dot = lineStyle.lineWidth
	let gap = 3 * lineStyle.lineWidth * gapMultiplier
	return {...lineStyle, lineDash: [dash, gap, dot, gap, dot, gap]}
}

let dottedPrimary = function (lineStyle){
	return {...lineStyle, primaryStyle: dotted(lineStyle.primaryStyle)}
}

let dashedPrimary = function (lineStyle){
	return {...lineStyle, primaryStyle: dashed(lineStyle.primaryStyle)}
}
let dotDashedPrimary = function (lineStyle){
	return {...lineStyle, primaryStyle: dotDashed(lineStyle.primaryStyle)}
}
let dotDotDashedPrimary = function (lineStyle, gapMultiplier){
	return {...lineStyle, primaryStyle: dotDotDashed(lineStyle.primaryStyle, gapMultiplier)}
}


let lineStyleFamily = {
	primaryStyle: _styleFamily,
	secondaryStyle: _styleDefault
}
let lineStyleFamily2 = {
	primaryStyle: _styleFamily2,
	secondaryStyle: _styleDefault
}
let lineStyleFamily3 = {
	primaryStyle: _styleFamily3,
	secondaryStyle: _styleDefault
}

let lineStyleFamily2Dotted = dottedPrimary(lineStyleFamily2)
let lineStyleFamily2Dashed = dashedPrimary(lineStyleFamily2)
let lineStyleFamily2DotDashed = dotDashedPrimary(lineStyleFamily2)
let lineStyleFamily3DotDashed = dotDashedPrimary(lineStyleFamily3)
let lineStyleFamily2DotDotDashed = dotDotDashedPrimary(lineStyleFamily2)
let lineStyleFamily2DotDotDashed2 = dotDotDashedPrimary(lineStyleFamily2, 3)


let lineStyleDefault = {
	primaryStyle: _styleDefault,
	secondaryStyle: _styleDefault
}

let lineStyleAdopted = {
	primaryStyle: _styleFamily2,
	secondaryStyle: _styleFamily2
}
let lineStyleFoster = {
	primaryStyle: _styleHarmony,
	secondaryStyle: _styleHarmony
}


let lineStyleHarmony = {
	primaryStyle: _styleHarmony,
	secondaryStyle: _styleHarmony
}

let lineStyleConflict = {
	primaryStyle: _styleConflict,
	secondaryStyle: _styleDefault
}
let lineStyleConflict2 = {
	primaryStyle: _styleConflict,
	secondaryStyle: _styleConflict
}
let lineStyleAbuse = {
	primaryStyle: _styleAbuse,
	secondaryStyle: _styleDefault
}
let lineStyleAbuse2 = {
	primaryStyle: _styleAbuse,
	secondaryStyle: _styleAbuse
}
let straightLine = new LineTypeItem("Biological child", 'straight', lineStyleDefault, GenogramConnectionCategoryEnum.CHILD)
let adoptedLine = new LineTypeItem("Adopted child", 'adopted', lineStyleAdopted, GenogramConnectionCategoryEnum.CHILD)
let fosterLine = new LineTypeItem("Foster child", 'foster', lineStyleFoster, GenogramConnectionCategoryEnum.CHILD)
let immigrationLine = new LineTypeItem("Immigration", 'immigration', lineStyleDefault, GenogramConnectionCategoryEnum.CHILD)
let multiculturalLine = new LineTypeItem("Multicultural", 'multicultural', lineStyleDefault, GenogramConnectionCategoryEnum.CHILD)


let lineTypeGeneralItems = [
	straightLine,
	adoptedLine,
	fosterLine,
	multiculturalLine,
	immigrationLine,
]

let lineTypeFamilyItems = [
	new LineTypeItem("Marriage", 'family-marriage', lineStyleDefault, GenogramConnectionCategoryEnum.SOCIAL),
	new LineTypeItem("Divorce", 'family-divorce', lineStyleFamily, GenogramConnectionCategoryEnum.SOCIAL),
	new LineTypeItem("Separation in fact", 'family-separation', lineStyleFamily, GenogramConnectionCategoryEnum.SOCIAL),
	new LineTypeItem("Legal Separation", 'family-legal-separation', lineStyleFamily, GenogramConnectionCategoryEnum.SOCIAL),

	new LineTypeItem("Engagement", 'family-engagement', lineStyleFamily2Dashed, GenogramConnectionCategoryEnum.SOCIAL),
	new LineTypeItem("Engagement and Cohabitation", 'family-engagement-cohabitation', lineStyleFamily2Dashed, GenogramConnectionCategoryEnum.SOCIAL),
	new LineTypeItem("Engagement and Separation", 'family-engagement-separation', lineStyleFamily2Dashed, GenogramConnectionCategoryEnum.SOCIAL),
	new LineTypeItem("Nullity", 'family-nullity', lineStyleFamily, GenogramConnectionCategoryEnum.SOCIAL),

	new LineTypeItem("Legal Cohabitation", 'family-legal-cohabitation', lineStyleFamily2DotDashed, GenogramConnectionCategoryEnum.SOCIAL),
	new LineTypeItem("Legal Cohabitation and Separation in Fact", 'family-legal-cohabitation-separation', lineStyleFamily2DotDashed, GenogramConnectionCategoryEnum.SOCIAL),
	new LineTypeItem("Legal Cohabitation and Legal Separation", 'family-legal-cohabitation-legal-separation', lineStyleFamily2DotDashed, GenogramConnectionCategoryEnum.SOCIAL),
	new LineTypeItem("Long-term Relationship", 'family-long-term-relationship', lineStyleFamily2Dashed, GenogramConnectionCategoryEnum.SOCIAL),

	new LineTypeItem("Cohabitation", 'family-cohabitation', lineStyleFamily2DotDotDashed, GenogramConnectionCategoryEnum.SOCIAL),
	new LineTypeItem("Cohabitation and Separation", 'family-cohabitation-separation', lineStyleFamily2DotDotDashed, GenogramConnectionCategoryEnum.SOCIAL),
	new LineTypeItem("Non-sentimental Cohabitation", 'family-non-sentimental-cohabitation', lineStyleFamily2DotDotDashed2, GenogramConnectionCategoryEnum.SOCIAL),
	new LineTypeItem("Non-sentimental Cohabitation and Separation", 'family-non-sentimental-cohabitation-separation', lineStyleFamily2DotDotDashed2, GenogramConnectionCategoryEnum.SOCIAL),

	new LineTypeItem("Casual Relationship", 'family-casual', lineStyleFamily2Dotted, GenogramConnectionCategoryEnum.SOCIAL),
	new LineTypeItem("Casual Relationship and Separation", 'family-casual-separation', lineStyleFamily2Dotted, GenogramConnectionCategoryEnum.SOCIAL),
	new LineTypeItem("Temporary relation", 'family-one-night-stand', lineStyleFamily2DotDashed, GenogramConnectionCategoryEnum.SOCIAL),
	new LineTypeItem("Love Affair", 'family-love-affair', lineStyleFamily3DotDashed, GenogramConnectionCategoryEnum.SOCIAL),
]

let lineTypeEmotionalItems = [
	new LineTypeItem("Indifferent", 'indifferent', lineStyleDefault, GenogramConnectionCategoryEnum.EMOTIONAL),
	new LineTypeItem("Distant/Poor", 'distant', lineStyleDefault, GenogramConnectionCategoryEnum.EMOTIONAL),
	new LineTypeItem("Cutoff", 'cutoff', lineStyleConflict2, GenogramConnectionCategoryEnum.EMOTIONAL),
	new LineTypeItem("Conflict", 'discord', lineStyleConflict2, GenogramConnectionCategoryEnum.EMOTIONAL),
	new LineTypeItem("Hate", 'hate', lineStyleConflict2, GenogramConnectionCategoryEnum.EMOTIONAL),

	new LineTypeItem("Harmony", 'harmony1', lineStyleHarmony, GenogramConnectionCategoryEnum.EMOTIONAL),
	new LineTypeItem("Close", 'harmony2', lineStyleHarmony, GenogramConnectionCategoryEnum.EMOTIONAL),
	new LineTypeItem("Very Close", 'harmony3', lineStyleHarmony, GenogramConnectionCategoryEnum.EMOTIONAL),
	new LineTypeItem("Love", 'harmony4', lineStyleHarmony, GenogramConnectionCategoryEnum.EMOTIONAL),
	new LineTypeItem("In Love", 'harmony5', lineStyleHarmony, GenogramConnectionCategoryEnum.EMOTIONAL),

	new LineTypeItem("Hostile", 'hostile1', oriented(lineStyleConflict), GenogramConnectionCategoryEnum.EMOTIONAL),
	new LineTypeItem("Distant-Hostile", 'hostile2', oriented(lineStyleConflict), GenogramConnectionCategoryEnum.EMOTIONAL),
	new LineTypeItem("Close-Hostile", 'hostile3', oriented(lineStyleConflict), GenogramConnectionCategoryEnum.EMOTIONAL),
	new LineTypeItem("Fused-Hostile", 'hostile4', oriented(lineStyleConflict), GenogramConnectionCategoryEnum.EMOTIONAL),
	new LineTypeItem("Distrust", 'hostile5', oriented(lineStyleConflict), GenogramConnectionCategoryEnum.EMOTIONAL),

	new LineTypeItem("Violence", 'violence1', oriented(lineStyleConflict), GenogramConnectionCategoryEnum.EMOTIONAL),
	new LineTypeItem("Distant-Violence", 'violence2', oriented(lineStyleConflict), GenogramConnectionCategoryEnum.EMOTIONAL),
	new LineTypeItem("Close-Violence", 'violence3', oriented(lineStyleConflict), GenogramConnectionCategoryEnum.EMOTIONAL),
	new LineTypeItem("Fused-Violence", 'violence4', oriented(lineStyleConflict), GenogramConnectionCategoryEnum.EMOTIONAL),
	new LineTypeItem("Fused", 'violence5', oriented(lineStyleConflict2), GenogramConnectionCategoryEnum.EMOTIONAL),

	new LineTypeItem("Abuse", 'abuse1', oriented(lineStyleAbuse), GenogramConnectionCategoryEnum.EMOTIONAL),
	new LineTypeItem("Physical Abuse", 'abuse2', oriented(lineStyleAbuse), GenogramConnectionCategoryEnum.EMOTIONAL),
	new LineTypeItem("Emotional Abuse", 'abuse3', oriented(lineStyleAbuse), GenogramConnectionCategoryEnum.EMOTIONAL),
	new LineTypeItem("Sexual Abuse", 'abuse4', oriented(lineStyleAbuse), GenogramConnectionCategoryEnum.EMOTIONAL),
	new LineTypeItem("Neglect (Abuse)", 'abuse5', oriented(lineStyleAbuse2), GenogramConnectionCategoryEnum.EMOTIONAL),

	new LineTypeItem("Manipulative", 'manipulative', oriented(lineStyleConflict2), GenogramConnectionCategoryEnum.EMOTIONAL),
	new LineTypeItem("Controlling", 'controlling', oriented(lineStyleConflict2), GenogramConnectionCategoryEnum.EMOTIONAL),
	new LineTypeItem("Focused On", 'focused-on', oriented(lineStyleDefault), GenogramConnectionCategoryEnum.EMOTIONAL),
	new LineTypeItem("Fan/Admirer", 'admirer', oriented(lineStyleDefault), GenogramConnectionCategoryEnum.EMOTIONAL),
	new LineTypeItem("Limerence", 'limerence', oriented(lineStyleDefault), GenogramConnectionCategoryEnum.EMOTIONAL),
]

let lineTypeDefaultItems = [
	lineTypeFamilyItems[0],
	lineTypeFamilyItems[1],
	...lineTypeEmotionalItems
]


let lineTypeAll = []

lineTypeFamilyItems.forEach(item => lineTypeAll.push(item))
lineTypeEmotionalItems.forEach(item => lineTypeAll.push(item))
lineTypeGeneralItems.forEach(item => lineTypeAll.push(item))

let findLineTypeItemByLineType = function (lineType){
	let index = lineTypeAll.findIndex(item => item.lineType === lineType)
	if(index >= 0){
		return lineTypeAll[index]
	}
	return null
}

let resolveLineTypeItemByLineType = function (lineType, customConnectionsSyncManager = null){
	let builtInItem = findLineTypeItemByLineType(lineType)
	if(builtInItem){
		return builtInItem
	}
	return customConnectionsSyncManager?.createLineTypeItem?.(customConnectionsSyncManager?.getByLineSubType?.(lineType)) || null
}

let findEmotionalLineTypeItemByLineType = function (lineType){
	let index = lineTypeEmotionalItems.findIndex(item => item.lineType === lineType)
	if(index >= 0){
		return lineTypeEmotionalItems[index]
	}
	return null
}

let resolveEmotionalLineTypeItemByLineType = function (lineType, customConnectionsSyncManager = null){
	let builtInItem = findEmotionalLineTypeItemByLineType(lineType)
	if(builtInItem){
		return builtInItem
	}
	return customConnectionsSyncManager?.createLineTypeItem?.(customConnectionsSyncManager?.getByLineSubType?.(lineType)) || null
}

let findFamilyLineTypeItemByLineType = function (lineType){
	let index = lineTypeFamilyItems.findIndex(item => item.lineType === lineType)
	if(index >= 0){
		return lineTypeFamilyItems[index]
	}
	index = lineTypeGeneralItems.findIndex(item => item.lineType === lineType)
	if(index >= 0){
		return lineTypeGeneralItems[index]
	}
	return null
}


export {
	LineTypeItem,
	lineTypeDefaultItems,
	lineTypeEmotionalItems,
	lineTypeFamilyItems,
	lineTypeGeneralItems,
	findLineTypeItemByLineType,
	resolveLineTypeItemByLineType,
	straightLine,
	findEmotionalLineTypeItemByLineType,
	findFamilyLineTypeItemByLineType,
	resolveEmotionalLineTypeItemByLineType
}
